import { useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { GradeReport } from './components/GradeReport';
import { AndroidIcon, AppleIcon, EvoLogoFullSvg } from './components/icons';
import { getGradeShare } from './lib/data';
import type { GradeShareData } from './lib/types';
import { APP_DOWNLOAD_URL, APP_STORE_URL, GOOGLE_PLAY_URL } from '../appLinks';
import './share.css';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; data: GradeShareData }
  | { status: 'not-found' }
  | { status: 'error' };

export default function SharePage() {
  const code = useMemo(() => {
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    const [, rawCode = ''] = path.split('/');
    return decodeURIComponent(rawCode).trim();
  }, []);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await getGradeShare(code);
        if (cancelled) return;
        if (!data) {
          setState({ status: 'not-found' });
          return;
        }
        document.title = `${data.card.name} grade report | Evo`;
        setState({ status: 'loaded', data });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <div className="share-root">
      {state.status === 'loaded' ? (
        <GradeReport data={state.data} />
      ) : (
        <ShareState state={state.status} />
      )}
    </div>
  );
}

function ShareState({ state }: { state: Exclude<LoadState['status'], 'loaded'> }) {
  const title =
    state === 'loading'
      ? 'Loading grade'
      : state === 'error'
        ? 'Unable to load grade'
        : 'Grade not found';
  const body =
    state === 'loading'
      ? 'Fetching this public Evo grade report.'
      : state === 'error'
        ? 'The grade report could not be loaded. Try again in a moment.'
        : 'This share link is unavailable or has been taken down.';

  return (
    <main className="page-shell share-state">
      <div className="share-state-logo">
        <EvoLogoFullSvg height={32} />
      </div>
      <h1 className="share-state-title">{title}</h1>
      <p className="share-state-body">{body}</p>
      {state !== 'loading' ? <DownloadAppCard /> : null}
    </main>
  );
}

function DownloadAppCard() {
  return (
    <section className="share-download-card" aria-label="Download Evo">
      <div className="share-download-copy">
        <h2>Get Evo</h2>
        <p>Grade cards, track your collection, and share reports from the mobile app.</p>
      </div>
      <div className="share-download-qr" aria-label="Scan to download Evo">
        <QRCodeSVG value={APP_DOWNLOAD_URL} size={132} bgColor="#ffffff" fgColor="#07001a" level="M" />
      </div>
      <div className="share-store-links">
        <a href={APP_STORE_URL} rel="noopener noreferrer" target="_blank">
          <AppleIcon size={18} />
          App Store
        </a>
        <a href={GOOGLE_PLAY_URL} rel="noopener noreferrer" target="_blank">
          <AndroidIcon size={18} />
          Google Play
        </a>
      </div>
    </section>
  );
}
